import React from 'react'

import PropTypes from 'prop-types'
import { styled } from '@mui/material'
import classNames from 'classnames'
import { useTheme } from '@mui/material/styles'
import StrapiLink from '@templates/strapi-cms/content-types/Link'
import { useTest } from '@hooks/useTest'

const getID = (value = '', suffix = '') => {
  const id = value.toLowerCase().replace(/\s/g, '-')
  return suffix ? `${id}-${suffix}` : id
}

const ListItem = styled('li')`
  .nav-btn {
    padding: 12px;
    line-height: 1;
    font-size: 1rem;
    border-radius: 0;
    position: relative;
    display: block;
    font-weight: 600;
    color: ${p => p.theme.palette?.common?.black};
    text-decoration: none;

    &::after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 4px;
      left: 0;
      transition: background-color ${p => (p.isFirstOpenTab ? '0.4s' : '0s')} ease;
      background-color: transparent;
    }

    &.active {
      &::after {
        background-color: ${p => p.theme.palette?.warning?.main};
      }
    }

    &.open {
      &::after {
        background-color: ${p => p.theme.palette?.primary?.main};
      }
    }

    &:hover {
      color: ${p => p.theme.palette?.primary?.main};
      text-decoration: none;
    }

    ${p => p.theme.breakpoints.between('md', 1150)} {
      font-size: 0.85rem;
    }
  }
`

const isActive = link => {
  if (link && typeof window !== 'undefined') {
    return window.location.href.includes(link?.InternalUrl)
  }
  return false
}

const NavigationItem = ({
  isSelected,
  disableTabbing,
  isFirstOpenTab,
  navigationLink,
  setActiveSection,
  setIsOpen,
  isOpen,
  lastFocusedNavItemRef,
}) => {
  const theme = useTheme()
  const { generateTestId } = useTest()

  const primaryLink = navigationLink?.PrimaryLink?.Link
  const hasChildren = !!navigationLink?.NavLinks?.length
  const menuID = getID(primaryLink?.DisplayText, 'megamenu')
  const buttonID = getID(primaryLink?.DisplayText, 'category-navigation-item-link')
  const fontColor = Object.is(primaryLink?.DisplayText.toLowerCase(), 'sales')
    ? theme.palette?.error?.main // red
    : theme.palette?.common?.black // black

  const onNavItemClick = e => {
    if (typeof window !== 'undefined') {
      window.dataLayer.push({
        event: 'navigation_link',
        nav_type: 'desktop',
        nav_link_list: 'Primary Link',
        nav_section: `Nav Links : ${primaryLink?.DisplayText}`,
        nav_link: primaryLink?.Title || '',
        nav_route: primaryLink?.InternalUrl || '',
      })
    }
    e.stopPropagation()
    setIsOpen(false)
  }

  const onKeyDown = e => {
    if (e.code === 'Space' || e.key === 'Space' || e.code === 'Enter' || e.key === 'Enter') {
      e.preventDefault()
      if (hasChildren) {
        setIsOpen(true)
        setActiveSection(navigationLink)
        lastFocusedNavItemRef.current = e.currentTarget
      }
    }
  }

  const buttonAriaProps = {
    'aria-haspopup': 'true',
    'aria-label': primaryLink?.DisplayText,
    ...(isSelected
      ? {
          'aria-controls': menuID,
          'aria-expanded': 'true',
        }
      : {}),
  }

  return (
    <ListItem
      data-testid={generateTestId('navigation', 'main-category')}
      onMouseEnter={() => setActiveSection(navigationLink)}
      isFirstOpenTab={isFirstOpenTab}
    >
      <div id={buttonID}>
        <StrapiLink
          className={classNames('nav-btn', {
            open: isSelected,
            active: isActive(primaryLink),
          })}
          data={primaryLink}
          style={{ color: fontColor }}
          disableUnderline
          {...buttonAriaProps}
          data-testid={primaryLink?.testId}
          role="button"
          onKeyDown={onKeyDown}
          onClick={onNavItemClick}
          tabIndex={disableTabbing ? -1 : undefined}
        >
          {primaryLink?.DisplayText?.toUpperCase()}
        </StrapiLink>
      </div>
    </ListItem>
  )
}

NavigationItem.propTypes = {
  isSelected: PropTypes.bool,
  disableTabbing: PropTypes.bool,
  isFirstOpenTab: PropTypes.bool,
  navigationLink: PropTypes.shape({
    id: PropTypes.string,
    PrimaryLink: PropTypes.shape({
      Link: PropTypes.shape({
        DisplayText: PropTypes.string,
        Title: PropTypes.string,
        InternalUrl: PropTypes.string,
        testId: PropTypes.string,
      }),
    }),
    NavLinks: PropTypes.array,
  }).isRequired,
  setActiveSection: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  lastFocusedNavItemRef: PropTypes.object.isRequired,
}

export default NavigationItem
